import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageData } from '@/_metronic/layout/core/PageData';
import TicketComposerModal from '../components/TicketComposerModal';
import TicketOpenedModal from '../modal/TicketOpenedModal';
import { type Ticket, TicketComposerStep, type TicketOpenedHandler, type TicketPrefill } from '../core/_models';
import { TicketComposerContext, type TicketComposerOpenOptions } from './TicketComposerContext';

type TicketComposerProviderProps = {
  children: ReactNode;
};

type TicketComposerRequest = {
  openCount: number;
  step: TicketComposerStep;
  prefill?: TicketPrefill;
  origin: string;
  onTicketOpened?: TicketOpenedHandler;
};

const TicketComposerProvider = ({ children }: TicketComposerProviderProps) => {
  const { pathname } = useLocation();
  const { pageTitle = '' } = usePageData();
  const [show, setShow] = useState(false);
  const [openedTicket, setOpenedTicket] = useState<Ticket>();
  const [request, setRequest] = useState<TicketComposerRequest>({
    openCount: 0,
    step: TicketComposerStep.SUBJECT,
    origin: '',
  });
  const pathnameRef = useRef(pathname);
  const pageTitleRef = useRef(pageTitle);
  pathnameRef.current = pathname;
  pageTitleRef.current = pageTitle;

  const openComposer = useCallback((options?: TicketComposerOpenOptions) => {
    const origin = `${pageTitleRef.current} (${pathnameRef.current})`;
    setRequest((previous) => ({
      openCount: previous.openCount + 1,
      step: options?.step ?? TicketComposerStep.SUBJECT,
      prefill: options?.prefill,
      origin: options?.origin ?? origin,
      onTicketOpened: options?.onTicketOpened,
    }));
    setShow(true);
  }, []);

  const closeComposer = () => setShow(false);
  const closeOpenedTicket = () => setOpenedTicket(undefined);

  const concludeOpening = (ticket: Ticket) => {
    setOpenedTicket(ticket);
    request.onTicketOpened?.(ticket);
  };
  const value = useMemo(() => ({ openComposer }), [openComposer]);
  const hasOpened = request.openCount > 0;

  return (
    <TicketComposerContext.Provider value={value}>
      {children}
      {hasOpened ? (
        <TicketComposerModal
          key={request.openCount}
          initialStep={request.step}
          origin={request.origin}
          prefill={request.prefill}
          show={show}
          onClose={closeComposer}
          onTicketOpened={concludeOpening}
        />
      ) : null}
      {openedTicket ? <TicketOpenedModal ticket={openedTicket} onClose={closeOpenedTicket} /> : null}
    </TicketComposerContext.Provider>
  );
};

export { TicketComposerProvider };
